import type { FormButtonSettings } from '@components/sp-form-button-settings';

import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from '@moddi3/ngx-intl-tel-input';
import { CountryISOService } from '@common/services/REST/country-iso.http.service';

import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
    EFormInputType,
    EPaymentType,
    ESize,
} from '@common/enums';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';

import { type PaymentFormWidgetData } from '@web/widgets/payment-form/type';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';
import { ProductType } from '@web/widgets/payment-form/type/payment-form-widget.type';
import { type AbstractControl, FormsModule } from '@angular/forms';
import type { PaymentElement, PaymentMethod } from '@integrations/payment-methods';
import { paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import type { Option } from '@common/types/form-element.type';
import { SpSafeHtmlPipe } from '@common/pipes/safe-html.pipe';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';

import type { WebLegacyWidgetEntity } from '@web/types';

import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getFormAlignCssVars,
    getFormButtonCssClasses,
    getFormButtonCssVars,
    getFormInputCssClasses,
    getFormInputCssVars,
    getFormRootCssClasses,
    getFormSizeCssVars,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getPaymentDescriptionCssVars,
    getPaymentFormRootCssVars,
} from '@sites/entity-core/src/widget/payment-form/styles/payment-form.css-vars';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';
import { FORM_ROOT_THEME_TOKENS, colorCss } from '@web/widgets/common/css-vars/css-vars.utils';

@Component({
    selector: 'web-payment-static-form-view',
    templateUrl: './payment-form-static-view.component.html',
    styleUrls: ['./payment-form-static-view.component.less'],
    imports: [
        FormsModule,
        NgSelectModule,
        TranslateModule,
        RemovePTags,
        SpSafeHtmlPipe,
        NgxIntlTelInputModule,
        SpIntlTelInputA11yDirective,
    ],
})
export class PaymentFormStaticViewComponent {
    protected readonly sanitizer = inject(DomSanitizer);
    private readonly countryISOService = inject(CountryISOService);

    public readonly widgetData = input.required<PaymentFormWidgetData>();
    public readonly isZero = input<boolean>(false);
    public readonly isMobile = input<boolean>(false);
    public readonly isTablet = input<boolean>(false);

    public typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;

    // Masked-phone (ngx-intl-tel-input) preview config — mirrors the form widget.
    public readonly SearchCountryField = SearchCountryField;
    public readonly PhoneNumberFormat = PhoneNumberFormat;
    public readonly CountryISO = signal<CountryISO>(CountryISO.Ukraine);
    public readonly excludeCountries = [CountryISO.Russia];

    constructor() {
        this.countryISOService
            .getCountryISO()
            .pipe(takeUntilDestroyed())
            .subscribe((data) => this.CountryISO.set(data.country_code.toLowerCase()));
    }

    public readonly formButtonSettings = computed<FormButtonSettings>(() => this.widgetData().formButtonSettings);

    // --- CSS custom properties (cascade from the form root) ---

    public readonly rootCssVars = computed<FormCssVars>(() => {
        const settings = this.widgetData().inputSettings;
        // The root carries the rotation/font/divider vars (envelope is rebuilt
        // locally — only `type` + `data.position` are read) plus the cascading
        // input/size/alignment/description vars.
        const envelope = {
            type: this.isZero() ? 'zero' : undefined,
            data: this.widgetData(),
        } as unknown as WebLegacyWidgetEntity<PaymentFormWidgetData>;

        return {
            ...getPaymentFormRootCssVars(envelope, FORM_ROOT_THEME_TOKENS),
            ...getFormInputCssVars(settings, colorCss),
            ...getFormSizeCssVars(settings),
            ...getFormAlignCssVars(settings),
            ...getPaymentDescriptionCssVars(settings, colorCss),
        };
    });

    public readonly rootCssClasses = computed<string>(() => getFormRootCssClasses(this.widgetData().inputSettings));

    public readonly fieldCssClasses = computed<string>(() => getFormInputCssClasses(this.widgetData().inputSettings));

    public readonly buttonCssVars = computed<FormCssVars>(() =>
        getFormButtonCssVars(this.formButtonSettings(), this.widgetData().inputSettings.formStyleType, colorCss)
    );

    public getButtonEffectClass(button: FormButtonSettings): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects?.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects?.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonStyles(formButtonSettings: FormButtonSettings): string {
        const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[formButtonSettings.size ? formButtonSettings.size : ESize.M]} form-button`;
        const themed = getFormButtonCssClasses(formButtonSettings);
        const type =
            formButtonSettings.styleType === EButtonStyleType.custom
                ? ''
                : BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType];

        return `${base} ${type} ${themed} ${this.getButtonEffectClass(formButtonSettings)}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getControlValue(control: AbstractControl, controlName: string) {
        return control.get(controlName)?.value;
    }

    public readonly productType = computed<PaymentFormWidgetData['productType']>(() => this.widgetData().productType);

    public readonly isProductTypeDefault = computed<boolean>(() => this.productType() === ProductType.default);

    public readonly isProductTypeEduCourse = computed<boolean>(() => this.productType() === ProductType.eduCourse);

    public readonly paymentElement = computed<PaymentFormWidgetData['paymentElements'][0]>(() =>
        this.widgetData().paymentElements.find((el) => el.isActive)
    );

    public readonly elements = computed<PaymentFormWidgetData['elements']>(() => this.widgetData().elements);

    public readonly inputSettings = computed<PaymentFormWidgetData['inputSettings']>(
        () => this.widgetData().inputSettings
    );

    public getPaymentIconSrc(type: PaymentMethod['type']): string {
        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[type]}-sm.svg`;
    }

    protected readonly paymentsList = paymentsList;

    public getPlaceholder(options: Option[] = []): string {
        const arr = options.filter((el) => el.selected).map((el) => el.label);

        return arr.length ? arr.join(', ') : 'select_value';
    }

    public readonly productSettings = computed(() => this.widgetData().productSettings);

    public readonly isPromoCodeEnabled = computed<boolean>(
        () => this.widgetData()?.additionalFormSettings?.promoCodeSettings?.isEnabled
    );

    public readonly promoCodeLabel = computed<string>(
        () => this.widgetData()?.additionalFormSettings?.promoCodeSettings?.label
    );

    public readonly promoCodePlaceholder = computed<string>(
        () => this.widgetData()?.additionalFormSettings?.promoCodeSettings?.placeholder
    );

    public readonly paymentElements = computed<PaymentFormWidgetData['paymentElements']>(
        () => this.widgetData().paymentElements
    );

    public getPaymentElementLabel(paymentElementType: PaymentElement['type'], index: number): string {
        if (paymentElementType === EPaymentType.custom) {
            return this.paymentElements().at(index).customPaymentName;
        }

        return this.paymentElements().at(index)?.newPaymentName || this.paymentsList[paymentElementType]?.label;
    }
}
